/**
 * Reaction block (spec §7.4.9, §8.9.2): the inline preview reads as a chemical equation —
 * identical components collapsed with a coefficient, `+` between same-side entries, one `→` with
 * agents above and conditions below. Edit opens the shared Ketcher panel; below the depiction,
 * collapsible groups edit coefficients, agents, conditions and quantities.
 */
import { Fragment, useEffect, useState, type ReactNode } from "react";
import type { LoroMap } from "loro-crdt";
import { openChemPanel } from "../../chem/panelStore";
import { canonicalSmiles, displayCoefficient, groupComponents, loadRDKit, moleculeSvg, splitRxn, type ComponentGroup } from "../../chem/rdkit";
import { useLoroSelector } from "../../sync/hooks";
import type { DocHandle } from "../../sync/docManager";
import type { BlockComponentProps } from "../registry";
import { LoroTextInput, bodyMap } from "../textField";
import {
  CHEMICAL_ENTITY,
  KM_ATMOSPHERE,
  KM_YIELD,
  QUANTITYKIND_NS,
  UNIT_NS,
  putEntry,
  readReaction,
  removeEntry,
  sameReaction,
  setCoefficient,
  unitSymbol,
  type AgentRole,
  type ReactionState,
} from "./model";
import { TermPicker } from "../../ui/TermPicker";
import { useT } from "../../i18n";

interface DrawnGroup extends ComponentGroup {
  svg: string;
}
interface Equation {
  reactants: DrawnGroup[];
  products: DrawnGroup[];
  agents: DrawnGroup[];
}

const CONDITION_EXTRA = [{ iri: KM_ATMOSPHERE, label: "atmosphere" }];
const QUANTITY_EXTRA = [{ iri: KM_YIELD, label: "yield (%)" }];

export function ReactionBlock({ handle, treeId, node, readOnly, onFocus }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const state = useLoroSelector(handle.doc, () => (body ? readReaction(body) : null), (a, b) => a === b || (!!a && !!b && sameReaction(a, b)));
  const [eq, setEq] = useState<Equation | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const rxn = state?.rxn ?? "";

  useEffect(() => {
    if (!rxn.trim()) return;
    let stop = false;
    void loadRDKit()
      .then((rdkit) => {
        if (stop) return;
        const parts = splitRxn(rxn);
        const draw = (blocks: string[], w: number, h: number): DrawnGroup[] =>
          groupComponents(blocks, (m) => canonicalSmiles(rdkit, m)).map((g) => ({ ...g, svg: moleculeSvg(rdkit, g.molblock, w, h) ?? "" }));
        setEq({ reactants: draw(parts.reactants, 160, 120), products: draw(parts.products, 160, 120), agents: draw(parts.agents, 90, 60) });
        setErr(null);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
    return () => {
      stop = true;
    };
  }, [rxn]);

  if (!body || !state) return null;
  return (
    <div className="rounded-md border border-neutral-200 p-2.5 dark:border-neutral-800" data-testid="reaction-block" data-fill-column onFocus={onFocus}>
      <div className="flex items-center gap-2 text-xs text-neutral-500">
        <span>{t("reaction.label")}</span>
        <div className="grow" />
        {!readOnly && (
          <button className="underline" data-testid="chem-edit" onClick={() => openChemPanel({ handle, treeId, body, blockType: "reaction" })}>
            {t("chem.edit")}
          </button>
        )}
      </div>
      {eq && rxn.trim() && <EquationView eq={eq} state={state} />}
      {!rxn.trim() && <div className="text-sm text-neutral-500">{readOnly ? t("reaction.empty") : t("reaction.emptyEdit")}</div>}
      {err && <div className="text-xs text-red-600">{err}</div>}
      <LoroTextInput handle={handle} map={body} field="caption" placeholder={t("media.caption")} readOnly={readOnly} testId="media-caption" />
      <SemanticsEditor handle={handle} body={body} state={state} eq={eq} readOnly={readOnly} />
    </div>
  );
}

function EquationView({ eq, state }: { eq: Equation; state: ReactionState }) {
  const side = (groups: DrawnGroup[], name: string) =>
    groups.map((g, i) => {
      const n = displayCoefficient(g, state.coefficients);
      return (
        <Fragment key={`${name}${i}`}>
          {i > 0 && (
            <span className="px-1 text-xl" data-testid="reaction-plus">
              +
            </span>
          )}
          <span className="inline-flex items-center" data-testid="reaction-component" data-smiles={g.smiles} data-side={name}>
            {n !== 1 && (
              <span className="pr-0.5 text-xl font-medium" data-testid="reaction-coefficient">
                {n}
              </span>
            )}
            {/* RDKit.js SVG is generated locally from the rxn (no server content) */}
            <span className="[&_svg]:max-w-full" dangerouslySetInnerHTML={{ __html: g.svg }} />
          </span>
        </Fragment>
      );
    });
  const authoredAgents = state.agents.map((a) => a.label || a.iri || a.role).filter(Boolean);
  const conditions = state.conditions.map((c) => [c.value, unitSymbol(c.unit)].filter(Boolean).join(" ")).filter(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-1 overflow-auto" data-testid="chem-preview" data-fill-surface data-fill-center>
      {side(eq.reactants, "reactant")}
      <span className="inline-flex flex-col items-center px-2 text-xs text-neutral-600 dark:text-neutral-300">
        <span className="flex items-end gap-1" data-testid="reaction-agents">
          {eq.agents.map((g, i) => (
            <span key={i} className="[&_svg]:max-w-full" data-smiles={g.smiles} dangerouslySetInnerHTML={{ __html: g.svg }} />
          ))}
          {authoredAgents.length > 0 && <span>{authoredAgents.join(", ")}</span>}
        </span>
        <span className="text-2xl leading-none" data-testid="reaction-arrow">
          →
        </span>
        <span data-testid="reaction-conditions">{conditions.join(", ")}</span>
      </span>
      {side(eq.products, "product")}
    </div>
  );
}

function Group({ title, count, children, testId }: { title: string; count: number; children: ReactNode; testId: string }) {
  return (
    <details className="mt-1 text-xs" data-testid={testId}>
      <summary className="cursor-pointer select-none text-neutral-500">
        {title}
        {count > 0 ? ` (${count})` : ""}
      </summary>
      <div className="mt-1 space-y-1 pl-3">{children}</div>
    </details>
  );
}

function SemanticsEditor({ handle, body, state, eq, readOnly }: { handle: DocHandle; body: LoroMap; state: ReactionState; eq: Equation | null; readOnly: boolean }) {
  const t = useT();
  const commit = (fn: () => void) => {
    fn();
    handle.doc.commit();
  };
  const species = state.rxn.trim() ? [...(eq?.reactants ?? []), ...(eq?.products ?? [])].filter((g) => g.smiles) : [];
  return (
    <div data-testid="reaction-semantics">
      <Group title={t("reaction.type")} count={state.reactionType ? 1 : 0} testId="reaction-type-group">
        <TermPicker
          value={{ iri: state.reactionType, label: "" }}
          types={["http://purl.obolibrary.org/obo/RXNO_0000001"]}
          placeholder={t("reaction.typePlaceholder")}
          readOnly={readOnly}
          testId="reaction-type"
          onPick={(term) => commit(() => body.set("reactionType", term.iri))}
        />
      </Group>

      <Group title={t("reaction.coefficients")} count={Object.keys(state.coefficients).length} testId="reaction-coefficients-group">
        {species.length === 0 && <div className="text-neutral-500">{t("reaction.drawFirst")}</div>}
        {species.map((g) => (
          <label key={g.smiles} className="flex items-center gap-2">
            <span className="w-40 truncate font-mono" title={g.smiles}>
              {g.smiles}
            </span>
            <CoefficientInput
              drawn={g.count}
              value={state.coefficients[g.smiles] ?? null}
              readOnly={readOnly}
              testId="reaction-coefficient-input"
              onChange={(v) => commit(() => setCoefficient(body, g.smiles, v))}
            />
          </label>
        ))}
      </Group>

      <Group title={t("reaction.agents")} count={state.agents.length} testId="reaction-agents-group">
        {state.agents.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-1" data-testid="reaction-agent-row">
            <RoleSelect value={a.role} readOnly={readOnly} onChange={(role) => commit(() => putEntry(body, "agents", { role, iri: a.iri, label: a.label }, a.id))} />
            <TermPicker
              value={{ iri: a.iri, label: a.label }}
              types={[CHEMICAL_ENTITY]}
              placeholder={t("reaction.species")}
              allowFree
              readOnly={readOnly}
              testId="reaction-agent-species"
              onPick={(term) => commit(() => putEntry(body, "agents", { role: a.role, iri: term.iri, label: term.label }, a.id))}
            />
            {!readOnly && <RemoveButton onClick={() => commit(() => removeEntry(body, "agents", a.id))} />}
          </div>
        ))}
        {!readOnly && (
          <button className="underline" data-testid="reaction-add-agent" onClick={() => commit(() => putEntry(body, "agents", { role: "solvent", iri: null, label: "" }))}>
            {t("reaction.addAgent")}
          </button>
        )}
      </Group>

      <Group title={t("reaction.conditions")} count={state.conditions.length} testId="reaction-conditions-group">
        {state.conditions.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-1" data-testid="reaction-condition-row">
            <TermPicker
              value={{ iri: c.kind || null, label: kindLabel(c.kind) }}
              ns={QUANTITYKIND_NS}
              extra={CONDITION_EXTRA}
              placeholder={t("reaction.kind")}
              readOnly={readOnly}
              testId="reaction-condition-kind"
              onPick={(term) => term.iri && commit(() => putEntry(body, "conditions", { ...strip(c), kind: term.iri! }, c.id))}
            />
            <ValueInput value={c.value} placeholder={t("reaction.value")} readOnly={readOnly} testId="reaction-condition-value" onChange={(value) => commit(() => putEntry(body, "conditions", { ...strip(c), value }, c.id))} />
            {c.kind !== KM_ATMOSPHERE && (
              <TermPicker
                value={{ iri: c.unit, label: unitSymbol(c.unit) }}
                ns={UNIT_NS}
                placeholder={t("reaction.unit")}
                readOnly={readOnly}
                testId="reaction-condition-unit"
                onPick={(term) => commit(() => putEntry(body, "conditions", { ...strip(c), unit: term.iri }, c.id))}
              />
            )}
            <ValueInput value={c.note} placeholder={t("reaction.note")} readOnly={readOnly} onChange={(note) => commit(() => putEntry(body, "conditions", { ...strip(c), note }, c.id))} />
            {!readOnly && <RemoveButton onClick={() => commit(() => removeEntry(body, "conditions", c.id))} />}
          </div>
        ))}
        {!readOnly && (
          <button className="underline" data-testid="reaction-add-condition" onClick={() => commit(() => putEntry(body, "conditions", { kind: "", value: "", unit: null, note: "" }))}>
            {t("reaction.addCondition")}
          </button>
        )}
      </Group>

      <Group title={t("reaction.quantities")} count={state.quantities.length} testId="reaction-quantities-group">
        {state.quantities.map((q) => (
          <div key={q.id} className="flex flex-wrap items-center gap-1" data-testid="reaction-quantity-row">
            <TermPicker
              value={{ iri: q.kind || null, label: kindLabel(q.kind) }}
              ns={QUANTITYKIND_NS}
              extra={QUANTITY_EXTRA}
              placeholder={t("reaction.kind")}
              readOnly={readOnly}
              testId="reaction-quantity-kind"
              onPick={(term) => term.iri && commit(() => putEntry(body, "quantities", { ...strip(q), kind: term.iri! }, q.id))}
            />
            <ValueInput value={q.value} placeholder={t("reaction.value")} readOnly={readOnly} onChange={(value) => commit(() => putEntry(body, "quantities", { ...strip(q), value }, q.id))} />
            {q.kind !== KM_YIELD && (
              <TermPicker
                value={{ iri: q.unit, label: unitSymbol(q.unit) }}
                ns={UNIT_NS}
                placeholder={t("reaction.unit")}
                readOnly={readOnly}
                onPick={(term) => commit(() => putEntry(body, "quantities", { ...strip(q), unit: term.iri }, q.id))}
              />
            )}
            {!readOnly && <RemoveButton onClick={() => commit(() => removeEntry(body, "quantities", q.id))} />}
          </div>
        ))}
        {!readOnly && (
          <button className="underline" data-testid="reaction-add-quantity" onClick={() => commit(() => putEntry(body, "quantities", { kind: "", value: "", unit: null }))}>
            {t("reaction.addQuantity")}
          </button>
        )}
      </Group>
    </div>
  );
}

/** Entry values are plain and replaced whole (§7.4.9 rule 4): drop the list-only `id`. */
function strip<T extends { id: string }>(entry: T): Omit<T, "id"> {
  const rest: Partial<T> = { ...entry };
  delete rest.id;
  return rest as Omit<T, "id">;
}

function kindLabel(kind: string): string {
  if (kind === KM_ATMOSPHERE) return "atmosphere";
  if (kind === KM_YIELD) return "yield (%)";
  return kind.startsWith(QUANTITYKIND_NS) ? kind.slice(QUANTITYKIND_NS.length) : kind;
}

function RoleSelect({ value, readOnly, onChange }: { value: AgentRole; readOnly: boolean; onChange(role: AgentRole): void }) {
  const t = useT();
  return (
    <select className="kss-control kss-field bg-transparent px-2 py-1 text-xs" value={value} disabled={readOnly} data-testid="reaction-agent-role" onChange={(e) => onChange(e.target.value as AgentRole)}>
      <option value="solvent">{t("reaction.role.solvent")}</option>
      <option value="catalyst">{t("reaction.role.catalyst")}</option>
      <option value="reagent">{t("reaction.role.reagent")}</option>
    </select>
  );
}

/** A text value committed on blur, so a half-typed number is kept as typed (§7.4.9). */
function ValueInput({ value, placeholder, readOnly, testId, onChange }: { value: string; placeholder?: string; readOnly: boolean; testId?: string; onChange(v: string): void }) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      className="w-24 kss-control kss-field bg-transparent px-2 py-1 text-xs"
      value={draft ?? value}
      placeholder={placeholder}
      readOnly={readOnly}
      data-testid={testId}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null && draft !== value) onChange(draft);
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

function CoefficientInput({ drawn, value, readOnly, testId, onChange }: { drawn: number; value: number | null; readOnly: boolean; testId?: string; onChange(v: number | null): void }) {
  const t = useT();
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      className="w-16 kss-control kss-field bg-transparent px-2 py-1 text-xs"
      type="number"
      min={0}
      step="any"
      placeholder={String(drawn)}
      title={t("reaction.drawnCount")}
      value={draft ?? (value === null ? "" : String(value))}
      readOnly={readOnly}
      data-testid={testId}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft === null) return;
        const n = Number(draft);
        onChange(draft.trim() === "" || !(n > 0) ? null : n);
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

function RemoveButton({ onClick }: { onClick(): void }) {
  const t = useT();
  return (
    <button className="px-1 text-neutral-500 hover:text-red-600" title={t("common.removeEntry")} onClick={onClick}>
      ×
    </button>
  );
}
